import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Avatar,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { strings } from 'i18n/Strings';
import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login, sendOtp } from 'service/AuthService';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { colorTheme } from 'theme';
import Logo from '../../../assets/images/Logo100X132.png';

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const SignIn = (): JSX.Element => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [passwordCheck, setPasswordCheck] = useState<string>('');
  const [loader, setLoader] = useState<boolean>(false);

  const dispatch = useDispatch();

  const handleInputChange = (
    type: string,
    e: { target: { value: string } },
  ): string | undefined => {
    switch (type) {
      case 'email': {
        setEmail(e.target.value.toLowerCase());
        return;
      }
      case 'password': {
        setPassword(e.target.value);
        return;
      }
      default:
        return '';
    }
  };

  const handleClick = (): void => {
    setShowPassword((prev) => !prev);
  };

  const validateEmail = (): void => {
    const lowerCaseEmail = email.toLowerCase();

    if (!lowerCaseEmail) {
      setEmailError(strings.displayText.emailMustText);
      return;
    }

    if (!EMAIL_REGEX.test(lowerCaseEmail)) {
      setEmailError(strings.displayText.emailValidateText);
      return;
    }

    setEmailError('');
  };

  const PasswordValidation = (): void => {
    if (password !== '') {
      if (passwordCheck) {
        if (passwordCheck === strings.displayText.setUpPasswordText) {
          setPasswordError(strings.displayText.setUpPasswordText);
        } else {
          setPasswordError(strings.displayText.invalidPasswordText);
        }
      } else {
        setPasswordError('');
      }
    } else {
      setPasswordError(strings.displayText.passwordMustValidationText);
    }
  };

  useEffect(() => {
    if (passwordCheck || password) {
      PasswordValidation();
    }
  }, [passwordCheck, password]);
  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();

    setPasswordCheck('');
    validateEmail();
    PasswordValidation();

    if (emailError || passwordCheck) {
      return;
    }

    try {
      setLoader(true);
      const lowerCaseEmail = email.toLowerCase();
      const res = await login(lowerCaseEmail, password);
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.loginSuccesText,
        }),
      );

      await sendOtp(lowerCaseEmail, 'email', res.token);
      navigate('/verifyotp', {
        state: { email: lowerCaseEmail, token: res.token },
      });
    } catch (err) {
      if (err?.response?.data?.message) {
        const errorMessage = err?.response?.data.message;

        if (errorMessage === strings.displayText.invalidEmailOrPasswordText) {
          setPasswordCheck(errorMessage);
        } else if (errorMessage === strings.displayText.setUpPasswordText) {
          setPasswordError(strings.displayText.setUpPasswordText);
          setPasswordCheck(errorMessage);
        } else {
          console.log(errorMessage);
        }
        dispatch(
          setSnackBarFailed({
            snackBarMessage: errorMessage,
          }),
        );
      } else {
        console.log(err);
      }
    } finally {
      setLoader(false);
    }
  };

  /* to prevent scenario where user enters credentials and moves to otp screen,
  presses back button to go to login screen and
  then presses forward button to move to otp screen */

  useEffect(() => {
    if (window.location.pathname === '/login') {
      window.history.pushState(null, document.title, window.location.href);
      window.addEventListener('popstate', function (event) {
        window.history.pushState(null, document.title, window.location.href);
      });
    }
  }, []);

  const loginStyles = {
    loginMainGrid: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: 350,
      marginX: 'auto',
    },
    avatar: {
      width: 100,
      height: 100,
      p: 2,
      bgcolor: colors.primaryBg[900],
      boxShadow: 3,
      position: 'relative',
      top: -48,
    },
    avatarImg: {
      width: 46,
      height: 56,
    },
    welcomeTypho: {
      fontFamily: 'Source Sans Pro !important',
      fontWeight: 700,
      color: colors.greenAccent[500],
    },
    investTypho: {
      color: colors.gray[500],
      fontWeight: 400,
      fontSize: 14,
    },
    submitBox: {
      mt: 1,
    },
    emailInput: {
      '& p': {
        color: 'red',
      },
      input: {
        '&:-webkit-autofill': {
          '-webkit-box-shadow': '0 0 0 100px #eef9fa inset',
          '-webkit-text-fill-color': colors.greenAccent[100],
        },
      },
    },
    passwordInput: {
      '& p': {
        color: 'red',
      },
      input: {
        '&:-webkit-autofill': {
          '-webkit-box-shadow': '0 0 0 100px #eef9fa inset',
          '-webkit-text-fill-color': colors.greenAccent[100],
        },
      },
    },
    submitBtn: {
      mt: 3,
      mb: 2,
      bgcolor: colors.greenAccent[500],
      borderRadius: 2,
      py: 1.5,
    },
    forgotPasswordGrid: {
      textAlign: 'center',
    },
  };

  return (
    <Fragment>
      <Box sx={loginStyles.loginMainGrid}>
        <Avatar alt="zen pensions logo" sx={loginStyles.avatar}>
          <img style={loginStyles.avatarImg} src={Logo} />
        </Avatar>
        <Typography component="h1" variant="h2" sx={loginStyles.welcomeTypho}>
          {strings.displayText.welcometoMarshmallow}
        </Typography>
        <Typography component="h5" variant="h5" sx={loginStyles.investTypho}>
          {strings.displayText.smartInvest}
        </Typography>
        <Box pb={5} />
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={loginStyles.submitBox}
        >
          <TextField
            margin="normal"
            required
            fullWidth
            label={strings.displayText.emailAddress}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleInputChange('email', e);
            }}
            sx={loginStyles.emailInput}
            helperText={emailError}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label={strings.displayText.passwordLabel}
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            autoCorrect="off"
            autoCapitalize="none"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleInputChange('password', e);
              setPasswordCheck('');
            }}
            helperText={passwordError}
            InputProps={{
              // <-- This is where the toggle button is added.
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClick}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={loginStyles.passwordInput}
          />

          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loading={loader}
            loadingPosition="end"
            sx={loginStyles.submitBtn}
          >
            <Typography component="h1" variant="h5">
              {strings.displayText.login}
            </Typography>
          </LoadingButton>
          <Grid container>
            <Grid item xs sx={loginStyles.forgotPasswordGrid}>
              <Link href="/forgotpassword" variant="body2" underline="none">
                <Typography component="h1" variant="h5">
                  {strings.displayText.forgotPassword}
                </Typography>
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Fragment>
  );
};

export default SignIn;
